import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { coreServicesApi } from '../lib/api/core-services.js';
import type { 
  CoreSystemHealth, 
  CoreSystemMetrics, 
  CoreIncident, 
  IncidentsResponse 
} from '../lib/api/types.js';

// Re-export types for consumers
export type SystemHealth = CoreSystemHealth;
export type SystemMetrics = CoreSystemMetrics;
export type Incident = CoreIncident;
export type { IncidentsResponse };

// Query keys for system health data
export const systemHealthKeys = {
  all: ['systemHealth'] as const,
  health: () => [...systemHealthKeys.all, 'health'] as const,
  metrics: () => [...systemHealthKeys.all, 'metrics'] as const,
  incidents: () => [...systemHealthKeys.all, 'incidents'] as const,
};

// Health check hook with auto-refresh
export const useSystemHealth = (refetchInterval = 30000) => {
  return useQuery<SystemHealth>({
    queryKey: systemHealthKeys.health(),
    queryFn: async () => {
      const response = await coreServicesApi.health.check();
      return response;
    },
    staleTime: 10 * 1000, // 10 seconds
    gcTime: 30 * 1000, // 30 seconds
    refetchInterval, // Auto-refresh every 30 seconds by default
    refetchIntervalInBackground: true,
  });
};

// Detailed metrics hook
export const useSystemMetrics = (refetchInterval = 60000) => {
  return useQuery<SystemMetrics>({
    queryKey: systemHealthKeys.metrics(),
    queryFn: async () => {
      const response = await coreServicesApi.health.metrics();
      return response;
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 60 * 1000, // 1 minute
    refetchInterval, // Auto-refresh every minute by default
    refetchIntervalInBackground: true,
  });
};

// Incidents hook
export const useIncidents = (refetchInterval = 30000) => {
  return useQuery<IncidentsResponse>({
    queryKey: systemHealthKeys.incidents(),
    queryFn: async () => {
      const response = await coreServicesApi.health.incidents();
      return response;
    },
    staleTime: 10 * 1000, // 10 seconds
    gcTime: 30 * 1000, // 30 seconds
    refetchInterval, // Auto-refresh every 30 seconds
    refetchIntervalInBackground: true,
  });
};

// Incident management mutations
export const useIncidentOperations = () => {
  const queryClient = useQueryClient();

  const createIncident = useMutation({
    mutationFn: async (incident: {
      title: string;
      severity: 'low' | 'medium' | 'high' | 'critical';
      description: string;
      affectedServices: string[];
    }) => {
      const response = await coreServicesApi.health.createIncident(incident);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: systemHealthKeys.incidents() });
      queryClient.invalidateQueries({ queryKey: systemHealthKeys.health() });
    },
  });

  const resolveIncident = useMutation({
    mutationFn: async (incidentId: string) => {
      const response = await coreServicesApi.health.resolveIncident(incidentId);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: systemHealthKeys.incidents() });
      queryClient.invalidateQueries({ queryKey: systemHealthKeys.health() });
    },
  });

  return {
    createIncident,
    resolveIncident,
    isLoading: createIncident.isPending || resolveIncident.isPending,
    error: createIncident.error || resolveIncident.error,
  };
};

// Helper hook to get system status summary
export const useSystemStatus = () => {
  const { data: health, isLoading: healthLoading } = useSystemHealth();
  const { data: metrics, isLoading: metricsLoading } = useSystemMetrics();
  const { data: incidents, isLoading: incidentsLoading } = useIncidents();

  const isOperational = health?.status === 'healthy';
  const isDegraded = health?.status === 'degraded';
  const isDown = health?.status === 'unhealthy';
  const hasActiveIncidents = (incidents?.active?.length || 0) > 0;

  return {
    status: health?.status || 'unknown',
    isOperational,
    isDegraded,
    isDown,
    hasActiveIncidents,
    activeIncidentCount: incidents?.active?.length || 0,
    requestsPerMinute: metrics?.requests?.requestsPerMinute || 0,
    errorRate: metrics?.requests?.errorRate || 0,
    averageResponseTime: metrics?.requests?.averageResponseTime || 0,
    cpuUsage: metrics?.system?.cpu?.usage || 0,
    memoryUsage: parseFloat(metrics?.system?.memory?.usagePercent || '0'),
    isLoading: healthLoading || metricsLoading || incidentsLoading,
  };
};
